import React from "react";
import { motion } from "framer-motion";
import { useT } from "../../i18n";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] } },
};

export default function HowItWorks() {
  const { t } = useT();
  const steps = t("howItWorks.steps") || [];
  const highlight = t("howItWorks.highlight");
  const closing2 = t("howItWorks.closing2");
  const closing2Html = closing2.replace(
    highlight,
    `<span class="text-[#3d4f42] italic font-serif">${highlight}</span>`
  );

  return (
    <section
      id="how-it-works"
      className="py-24 md:py-36 bg-[#f6f4ee]"
      data-testid="how-it-works-section"
    >
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
          className="max-w-3xl"
        >
          <div className="eyebrow mb-6">{t("howItWorks.sectionLabel")}</div>
          <h2 className="font-serif text-[#1a211c] tracking-tight leading-[1.05] text-[clamp(2rem,5vw,4.2rem)]">
            {t("howItWorks.title")}
          </h2>
          <p className="mt-8 text-[1.05rem] leading-relaxed text-[#1a211c]/75">
            {t("howItWorks.intro")}
          </p>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-14">
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeUp}
              transition={{ delay: i * 0.08 }}
              className="relative pt-8 border-t border-[#1a211c]/15"
              data-testid={`how-step-${s.n}`}
            >
              <div className="flex items-baseline justify-between mb-4">
                <span className="font-mono text-[0.75rem] tracking-[0.24em] text-[#3d4f42]">
                  STEP · {s.n}
                </span>
                <span className="font-serif text-[3.5rem] leading-none text-[#c5b293]/70 tabular">
                  {s.n}
                </span>
              </div>
              <h3 className="font-serif text-2xl md:text-3xl text-[#1a211c] leading-tight mb-3">
                {s.title}
              </h3>
              <p className="text-[0.98rem] leading-relaxed text-[#1a211c]/70">{s.text}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            variants={fadeUp}
            className="lg:col-span-6 space-y-5 text-[1rem] leading-relaxed text-[#1a211c]/80"
          >
            <p>{t("howItWorks.closing1")}</p>
            <p dangerouslySetInnerHTML={{ __html: closing2Html }} />
          </motion.div>
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            variants={fadeUp}
            className="lg:col-span-6 space-y-5 text-[1rem] leading-relaxed text-[#1a211c]/80"
          >
            <p>{t("howItWorks.closing3")}</p>
            <p>{t("howItWorks.closing4")}</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
